import PropTypes from 'prop-types'
import React from 'react'

import BusyOverlay from 'components/common/BusyOverlay'
import Text from 'components/common/Text'
import log from 'log'
import theme from 'theme'

type SectionProps = {
  children: React.ReactNode
  isLoading?: boolean
  hasError?: boolean
  errorMessage?: React.ReactNode
  darkTheme?: boolean
  renderError?: () => React.ReactNode
}

type SectionState = { hasError: boolean }

export default class Section extends React.Component<
  SectionProps,
  SectionState
> {
  static getDerivedStateFromError(): SectionState {
    // Update state so the next render will show the fallback UI.
    return { hasError: true }
  }

  componentDidCatch(error: Error): void {
    log.error(error)
  }

  state = {
    hasError: false
  }

  render(): JSX.Element {
    const {
      hasError,
      errorMessage,
      isLoading,
      children,
      darkTheme = false,
      renderError = null,
      ...rest
    } = this.props

    if (hasError || this.state.hasError) {
      return renderError ? (
        renderError()
      ) : (
        <ErrorSection darkTheme={darkTheme} message={errorMessage} />
      )
    }

    return (
      <section
        css={{
          display: 'flex',
          flexDirection: 'column',
          flex: 1
        }}
        {...rest}
      >
        {isLoading ? (
          <div css={{ position: 'relative', minHeight: 256 }}>
            <BusyOverlay shown transparentBg={darkTheme} />
          </div>
        ) : (
          children
        )}
      </section>
    )
  }
}

function ErrorSection({ message, darkTheme, ...rest }) {
  return (
    <div
      data-test-error-section
      css={{
        paddingTop: theme.space[5],
        paddingRight: 0,
        paddingBottom: theme.space[5],
        paddingLeft: 0
      }}
      {...rest}
    >
      <Text as="h5" color={darkTheme ? 'white' : undefined}>
        {message}
      </Text>
    </div>
  )
}

ErrorSection.propTypes = {
  darkTheme: PropTypes.bool,
  message: PropTypes.string
}

ErrorSection.defaultProps = {
  message:
    "We've encountered an error with this content. Please try again later."
}
